import { BankCardTransactionJSON } from '../entities/BankCardTransaction';
import { Currency } from '../entities/Currency';
import PartnerDocumentEntity, { PartnerDocumentJSON } from '../entities/PartnerDocument';
import { PartnerPaymentDocumentJSON } from '../entities/PartnerPaymentDocument';
import { RateJSON } from '../entities/Rate';
import { WireTransferJSON } from '../entities/WireTransfer';
import { CurrencyConversion } from './currencies';
import { getPriceInCurrency } from './prices';

export type PaymentStatus = 'TO_BE_INVOICED' | 'PROCESSING' | 'PAID_PARTIALLY' | 'PAID_FULLY';

interface GetTotalPaidAmountOptions extends Omit<CurrencyConversion, 'source'> {
  useReceivedAmountForWire?: boolean;
}

type BankCardTransaction = Pick<BankCardTransactionJSON, 'merchantAmount' | 'merchantCurrency' | 'fundDirection'>;

interface PartnerPayout {
  wireTransfer?: Pick<WireTransferJSON, 'status' | 'sentAmount' | 'sentCurrency' | 'receivedAmount' | 'receivedCurrency'> | null;
  bankCard?: { bankCardTransactions?: BankCardTransaction[] } | null;
}

interface PartnerDocument {
  partnersPaymentDocuments: Pick<PartnerPaymentDocumentJSON, 'amount' | 'currency'>[];
  partnersPayouts?: PartnerPayout[] | null;
}

interface ComputePaymentDocumentParams {
  total: number;
  document: Pick<PartnerPaymentDocumentJSON, 'amount' | 'currency'>;
  target: Currency;
  rate: Omit<RateJSON, 'date'>;
}

interface ComputePayoutParams {
  total: number;
  payout: PartnerPayout;
  target: Currency;
  rate: Omit<RateJSON, 'date'>;
  useReceivedAmountForWire: boolean;
}

interface ComputeCardTransactionParams {
  total: number;
  tx: BankCardTransaction;
  target: Currency;
  rate: Omit<RateJSON, 'date'>;
}

function computePaymentDocument({ total, document, target, rate }: ComputePaymentDocumentParams): number {
  const { value } = getPriceInCurrency(document.amount, { source: document.currency, target, rate });
  return total + value;
}

function computeCardTransaction({ total, tx, target, rate }: ComputeCardTransactionParams): number {
  const { value } = getPriceInCurrency(tx.merchantAmount, { source: tx.merchantCurrency, target, rate });

  if (tx.fundDirection === 'CREDIT') {
    return total - value;
  } else if (tx.fundDirection === 'DEBIT') {
    return total + value;
  }

  return total;
}

function computePayout({ total, payout, target, rate, useReceivedAmountForWire }: ComputePayoutParams): number {
  let currentPayoutAmount = 0;

  if (payout.wireTransfer && payout.wireTransfer.status === 'PAID') {
    const amount = useReceivedAmountForWire && payout.wireTransfer.receivedAmount != null ? payout.wireTransfer.receivedAmount : payout.wireTransfer.sentAmount;
    const currency = useReceivedAmountForWire && payout.wireTransfer.receivedCurrency != null ? payout.wireTransfer.receivedCurrency : payout.wireTransfer.sentCurrency;

    if (amount != null && currency) {
      const { value } = getPriceInCurrency(amount, { source: currency, target, rate });
      currentPayoutAmount += value;
    }
  }

  if (payout.bankCard && payout.bankCard.bankCardTransactions) {
    const cardTransactionsTotal = payout.bankCard.bankCardTransactions.reduce(
      (txTotal, tx) => computeCardTransaction({ total: txTotal, tx, target, rate }),
      0
    );

    currentPayoutAmount += cardTransactionsTotal;
  }

  return total + currentPayoutAmount;
}

export function getTotalInclTax(amounts: Pick<PartnerDocumentJSON, 'amount' | 'taxAmount' | 'currency'>[], { target, rate }: Omit<CurrencyConversion, 'source'>) {
  return amounts.reduce((total, { amount, taxAmount, currency }) => {
    const amountInCurrency = getPriceInCurrency(amount + taxAmount, { source: currency, target, rate });
    return total + amountInCurrency.value;
  }, 0);
}

export function getTotalInclRecoverableTax(partnersDocuments: Pick<PartnerDocumentEntity, 'amount' | 'taxAmount' | 'recoverableTax' | 'currency' | 'type'>[], { target, rate }: Omit<CurrencyConversion, 'source'>) {
  return partnersDocuments.reduce((total, { amount, taxAmount, recoverableTax, currency, type }) => {
    const totalInclRecoverableTax = amount + (recoverableTax ? 0 : taxAmount);
    const { value } = getPriceInCurrency(totalInclRecoverableTax, { source: currency, target, rate });

    return ['INVOICE', 'PROFORMA'].includes(type) ? total + value : total;
  }, 0);
}

export function getTotalPaidAmount(partnerDocument: PartnerDocument, { target, rate, useReceivedAmountForWire = false }: GetTotalPaidAmountOptions): number {
  const paymentDocumentsTotal = partnerDocument.partnersPaymentDocuments.reduce(
    (total, document) => computePaymentDocument({ total, document, target, rate }),
    0
  );

  const payoutsTotal = (partnerDocument.partnersPayouts || []).reduce(
    (total, payout) => computePayout({ total, payout, target, rate, useReceivedAmountForWire }),
    0
  );

  return paymentDocumentsTotal + payoutsTotal;
}

export interface PartnerDocumentTotalPayments {
  total: number;
  left: number;
  currency: Currency;
}

interface GetPartnerDocumentTotalPaymentsParams extends PartnerDocument {
  amount: number;
  taxAmount: number;
  currency: Currency;
  booking: { rate: Omit<RateJSON, 'date'> };
}

export function getPartnerDocumentTotalPayments(partnerDocument: GetPartnerDocumentTotalPaymentsParams, target?: Currency): PartnerDocumentTotalPayments {
  const currency = target ?? partnerDocument.currency;
  const { rate } = partnerDocument.booking;

  const total = getTotalPaidAmount(partnerDocument, { target: currency, rate, useReceivedAmountForWire: true });
  const { value: documentTotal } = getPriceInCurrency(partnerDocument.amount + partnerDocument.taxAmount, { source: partnerDocument.currency, target: currency, rate });

  return { total, left: documentTotal - total, currency };
}

export function getPaidPercentage(partnerDocuments: PartnerDocumentJSON[], rate: Omit<RateJSON, 'date'>): number {
  const totalAmount = getTotalInclTax(partnerDocuments, { target: 'EUR', rate });

  const paidAmount = partnerDocuments.reduce(
    (total, doc) => total + getTotalPaidAmount(doc, { target: 'EUR', rate, useReceivedAmountForWire: true }),
    0
  );

  return Math.round((paidAmount / totalAmount) * 100);
}

export function getPaymentStatus(partnerDocuments: PartnerDocumentJSON[], rate: RateJSON): PaymentStatus {
  if (partnerDocuments.length === 0) {
    return 'TO_BE_INVOICED';
  }

  const paidPercentage = getPaidPercentage(partnerDocuments, rate);

  if (paidPercentage === 0) {
    return 'PROCESSING';
  }

  return paidPercentage < 100 ? 'PAID_PARTIALLY' : 'PAID_FULLY';
}